import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { AppShell } from "../../components/AppShell";
import { PageHeader } from "../../components/PageHeader";
import { Card, ErrorBox, Skeleton } from "../../components/ui";
import {
  useEditableEmployee, useEmployeeFormOptions, useEndEmployment, useSaveEmployee, type EmploymentProblem,
} from "../../lib/api";
import { blocking, endsOnADate, groupOf, validateEmployee, type Problem } from "../../lib/employment";
import { AreaField, PickField, Section, TextField } from "./PersonFormFields";
import { PromotionPanel, ShiftPanel } from "./ShiftAndPromotion";

/**
 * Add or edit a person (WP-15 write screens, OS-0640).
 *
 * Until this screen every People page was read-only and an employee record could only be changed from the ERPNext
 * desk, which end users do not have. What made it worth building as its own screen rather than making the generated
 * ERP form editable is the type: the form asks for different things depending on whether somebody is permanent,
 * on a fixed term, or a trainee, and a generated form built from DocType metadata cannot know that a temporary
 * contract without an end date is not a contract.
 *
 * Rules run twice on purpose. `lib/employment.ts` runs them as you type, so the wrong field is marked; the endpoint
 * runs `engines/employment.py` again and refuses the save, so nothing here can be bypassed from the console.
 */

const TEXT = (v: unknown) => (v == null ? "" : String(v));

/** The four groups, in the order the form talks about them. `unset` is deliberately not offered: it is a state a
 * record can already be in (37 of them are), never one to choose. */
const GROUP_ORDER = ["permanent", "fixed_term", "trainee", "other"] as const;

export default function EmployeeForm() {
  const { employee } = useParams();
  const isNew = !employee;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const existing = useEditableEmployee(employee);
  const [values, setValues] = useState<Record<string, unknown>>({ status: "Active" });
  const [touched, setTouched] = useState(false);
  const [warnings, setWarnings] = useState<EmploymentProblem[]>([]);
  const options = useEmployeeFormOptions(TEXT(values.company) || undefined);
  const save = useSaveEmployee();
  const end = useEndEmployment();
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    if (existing.data) setValues({ ...existing.data.values });
  }, [existing.data]);

  const set = (field: string) => (v: string) => { setTouched(true); setValues((prev) => ({ ...prev, [field]: v })); };
  const problems: Problem[] = useMemo(() => validateEmployee(values, { name: employee }), [values, employee]);
  // nothing is marked before the person has touched the form: opening "add a person" already red is not feedback
  const shown = touched ? problems : [];
  const group = groupOf(TEXT(values.employment_type));
  const opts = options.data;

  const submit = () => {
    setTouched(true);
    if (blocking(problems).length > 0) return;
    save.mutate({ name: employee, values }, {
      onSuccess: (res) => {
        setWarnings(res.warnings ?? []);
        if (isNew) navigate(`/people/directory/${encodeURIComponent(res.name)}/edit`, { replace: true });
      },
    });
  };

  const pick = (list: string[] | undefined) => (list ?? []).map((v) => ({ value: v, label: v }));
  const title = isNew ? t("emp.form.addTitle") : (existing.data?.employee_name ?? employee ?? "");

  if (!isNew && existing.isLoading) {
    return <AppShell subtitle={t("ppl.title")}><div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div></AppShell>;
  }
  if (existing.isError) {
    return <AppShell subtitle={t("ppl.title")}><ErrorBox error={existing.error} onRetry={() => void existing.refetch()} /></AppShell>;
  }

  return (
    <AppShell subtitle={t("ppl.title")} record={isNew ? t("emp.form.addTitle") : (existing.data?.employee_name ?? undefined)}>
      <PageHeader title={title} subtitle={isNew ? t("emp.form.addHint") : t("emp.form.editHint")}
        actions={
          <div className="flex flex-wrap items-center gap-2" data-emp-form-actions>
            {!isNew && (
              <span className="rounded border border-brand-grey-light px-3 py-1.5 text-xs font-semibold hover:border-brand-primary" data-emp-view>
                {t("emp.form.viewRecord")}
              </span>
            )}
            <Link to="/people/directory" data-emp-cancel
              className="rounded border border-brand-grey-light px-3 py-1.5 text-xs font-semibold hover:border-brand-primary">
              {t("common.cancel")}
            </Link>
            <button type="button" onClick={submit} disabled={save.isPending} data-emp-save
              className="rounded bg-brand-primary px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
              {save.isPending ? t("emp.form.saving") : isNew ? t("emp.form.create") : t("emp.form.save")}
            </button>
          </div>
        } />

      {save.isError && <div className="mt-4"><ErrorBox error={save.error} /></div>}
      {touched && blocking(problems).length > 0 && (
        <Card className="mt-4 border-l-4 border-status-red bg-red-50/50 px-4 py-2 text-sm" data-emp-blocked>
          {t("emp.form.blocked", { n: blocking(problems).length })}
        </Card>
      )}
      {save.isSuccess && blocking(problems).length === 0 && (
        <Card className="mt-4 border-l-4 border-status-green bg-green-50/50 px-4 py-2 text-sm" data-emp-saved>
          {t("emp.form.saved")}
          {warnings.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-[11px] text-status-amber">
              {warnings.map((w) => <li key={w.code} data-emp-warning={w.code}>{t(`emp.problem.${w.code}`, (w.params ?? {}) as Record<string, unknown>)}</li>)}
            </ul>
          )}
        </Card>
      )}

      <div className="mt-4 space-y-4">
        <Section title={t("emp.form.identity")} hook="identity">
          <PickField field="salutation" label={t("emp.f.salutation")} problems={shown}
            value={TEXT(values.salutation)} onChange={set("salutation")} options={pick(opts?.salutations)} />
          <TextField field="first_name" label={t("emp.f.firstName")} required problems={shown}
            value={TEXT(values.first_name)} onChange={set("first_name")} />
          <TextField field="middle_name" label={t("emp.f.middleName")} problems={shown}
            value={TEXT(values.middle_name)} onChange={set("middle_name")} />
          <TextField field="last_name" label={t("emp.f.lastName")} problems={shown}
            value={TEXT(values.last_name)} onChange={set("last_name")} />
          <PickField field="gender" label={t("emp.f.gender")} required problems={shown}
            value={TEXT(values.gender)} onChange={set("gender")} options={pick(opts?.genders)} />
          <TextField field="date_of_birth" label={t("emp.f.dateOfBirth")} type="date" required problems={shown}
            value={TEXT(values.date_of_birth)} onChange={set("date_of_birth")} />
        </Section>

        <Section title={t("emp.form.engagement")} hint={t("emp.form.engagementHint")} hook="engagement">
          <PickField field="company" label={t("emp.f.company")} required problems={shown}
            value={TEXT(values.company)} onChange={set("company")}
            options={(opts?.companies ?? []).map((c) => ({ value: String(c.name), label: String(c.name) }))} />
          <PickField field="employment_type" label={t("emp.f.employmentType")} problems={shown}
            hint={t(`emp.group.${group}.hint`)}
            value={TEXT(values.employment_type)} onChange={set("employment_type")}
            options={GROUP_ORDER.flatMap((g) => (opts?.employment_types ?? []).filter((et) => et.group === g)
              .map((et) => ({ value: et.name, label: `${t(`emp.group.${g}`)} · ${et.name}` })))} />
          <PickField field="status" label={t("emp.f.status")} required problems={shown}
            value={TEXT(values.status) || "Active"} onChange={set("status")}
            options={(opts?.statuses ?? []).map((s) => ({ value: s, label: t(`emp.status.${s}`, s) }))} blank={undefined} />
          <TextField field="date_of_joining" label={t("emp.f.dateOfJoining")} type="date" required problems={shown}
            value={TEXT(values.date_of_joining)} onChange={set("date_of_joining")} />
          <TextField field="contract_end_date" label={t("emp.f.contractEnd")} type="date" problems={shown}
            required={endsOnADate(TEXT(values.employment_type))}
            hint={endsOnADate(TEXT(values.employment_type)) ? t("emp.form.endRequired") : undefined}
            value={TEXT(values.contract_end_date)} onChange={set("contract_end_date")} />
          <TextField field="final_confirmation_date" label={t("emp.f.confirmation")} type="date" problems={shown}
            value={TEXT(values.final_confirmation_date)} onChange={set("final_confirmation_date")} />
          <PickField field="designation" label={t("emp.f.designation")} problems={shown}
            value={TEXT(values.designation)} onChange={set("designation")} options={pick(opts?.designations)} />
          <PickField field="department" label={t("emp.f.department")} problems={shown}
            value={TEXT(values.department)} onChange={set("department")}
            options={(opts?.departments ?? []).map((d) => ({ value: d.name, label: d.name }))} />
          <PickField field="grade" label={t("emp.f.grade")} problems={shown}
            value={TEXT(values.grade)} onChange={set("grade")} options={pick(opts?.grades)} />
          <PickField field="branch" label={t("emp.f.branch")} problems={shown}
            value={TEXT(values.branch)} onChange={set("branch")} options={pick(opts?.branches)} />
          <TextField field="notice_number_of_days" label={t("emp.f.notice")} type="number" problems={shown}
            value={TEXT(values.notice_number_of_days)} onChange={set("notice_number_of_days")} />
          <TextField field="date_of_retirement" label={t("emp.f.retirement")} type="date" problems={shown}
            value={TEXT(values.date_of_retirement)} onChange={set("date_of_retirement")} />
        </Section>

        <Section title={t("emp.form.reaching")} hint={t("emp.form.reachingHint")} hook="reaching">
          <TextField field="cell_number" label={t("emp.f.mobile")} type="tel" problems={shown}
            value={TEXT(values.cell_number)} onChange={set("cell_number")} />
          <TextField field="company_email" label={t("emp.f.companyEmail")} type="email" problems={shown}
            value={TEXT(values.company_email)} onChange={set("company_email")} />
          <TextField field="personal_email" label={t("emp.f.personalEmail")} type="email" problems={shown}
            value={TEXT(values.personal_email)} onChange={set("personal_email")} />
          <TextField field="person_to_be_contacted" label={t("emp.f.emergencyName")} problems={shown}
            value={TEXT(values.person_to_be_contacted)} onChange={set("person_to_be_contacted")} />
          <TextField field="emergency_phone_number" label={t("emp.f.emergencyPhone")} type="tel" problems={shown}
            value={TEXT(values.emergency_phone_number)} onChange={set("emergency_phone_number")} />
          <TextField field="relation" label={t("emp.f.relation")} problems={shown}
            value={TEXT(values.relation)} onChange={set("relation")} />
          <AreaField field="current_address" label={t("emp.f.address")} problems={shown}
            value={TEXT(values.current_address)} onChange={set("current_address")} />
        </Section>

        <Section title={t("emp.form.workingTime")} hook="time">
          <PickField field="holiday_list" label={t("emp.f.holidayList")} problems={shown}
            value={TEXT(values.holiday_list)} onChange={set("holiday_list")} options={pick(opts?.holiday_lists)} />
          <PickField field="default_shift" label={t("emp.f.shift")} problems={shown}
            value={TEXT(values.default_shift)} onChange={set("default_shift")} options={pick(opts?.shift_types)} />
          <TextField field="attendance_device_id" label={t("emp.f.deviceId")} problems={shown}
            hint={t("emp.form.deviceHint")}
            value={TEXT(values.attendance_device_id)} onChange={set("attendance_device_id")} />
        </Section>

        {!isNew && (
          <Card className="border-l-4 border-brand-primary bg-blue-50/40 px-4 py-3 text-sm" data-emp-approvers-note>
            {t("emp.form.approversElsewhere")}{" "}
            <Link to="/admin?tab=config" className="font-semibold text-brand-primary hover:underline">{t("emp.form.approversLink")}</Link>
            {existing.data?.manager_name && <> · {t("emp.f.manager")}: <span className="font-semibold">{existing.data.manager_name}</span></>}
          </Card>
        )}

        {!isNew && <ShiftPanel employee={employee!} />}
        {!isNew && <PromotionPanel employee={employee!} grades={opts?.grades ?? []} designations={opts?.designations ?? []}
          current={{ grade: TEXT(values.grade), designation: TEXT(values.designation) }} />}
        {!isNew && <EndEmployment employee={employee!} values={values} open={ending} setOpen={setEnding}
          onDone={(res) => { setWarnings(res.warnings ?? []); setValues((p) => ({ ...p, status: res.status, relieving_date: res.relieving_date })); }}
          mutation={end} />}
      </div>
    </AppShell>
  );
}

/** Ending employment is its own action, not a status somebody edits: the exit queue, the settlement and the assets
 * register all read the date and the reason, and a leaver's reports do not reassign themselves. */
function EndEmployment({ employee, values, open, setOpen, onDone, mutation }: {
  employee: string;
  values: Record<string, unknown>;
  open: boolean;
  setOpen: (v: boolean) => void;
  onDone: (res: { status: string; relieving_date: string; warnings: EmploymentProblem[]; orphaned_reports: string[] }) => void;
  mutation: ReturnType<typeof useEndEmployment>;
}) {
  const { t } = useTranslation();
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [workplace, setWorkplace] = useState("");
  const [orphans, setOrphans] = useState<string[]>([]);
  const alreadyLeft = TEXT(values.status) === "Left";

  if (alreadyLeft) {
    return (
      <Card className="border-l-4 border-brand-grey px-4 py-3 text-sm" data-emp-already-left>
        {t("emp.end.already", { date: TEXT(values.relieving_date) || "—" })}
      </Card>
    );
  }
  return (
    <Card className="border-l-4 border-status-red px-4 py-3" data-emp-end>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-heading text-base font-semibold">{t("emp.end.title")}</h2>
          <p className="text-[11px] text-brand-grey">{t("emp.end.hint")}</p>
        </div>
        <button type="button" onClick={() => setOpen(!open)} data-emp-end-open
          className="rounded border border-status-red px-3 py-1.5 text-xs font-semibold text-status-red hover:bg-red-50">
          {open ? t("common.cancel") : t("emp.end.open")}
        </button>
      </div>
      {open && (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label><span className="block text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("emp.end.date")}</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-emp-end-date
              className="mt-1 w-full rounded border border-brand-grey-light px-2 py-1.5 text-sm" /></label>
          <label className="sm:col-span-2"><span className="block text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("emp.end.reason")}</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-emp-end-reason
              className="mt-1 w-full rounded border border-brand-grey-light px-2 py-1.5 text-sm" /></label>
          <label className="sm:col-span-2"><span className="block text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("emp.end.workplace")}</span>
            <input value={workplace} onChange={(e) => setWorkplace(e.target.value)} data-emp-end-workplace
              className="mt-1 w-full rounded border border-brand-grey-light px-2 py-1.5 text-sm" /></label>
          <div className="flex items-end">
            <button type="button" disabled={!date || !reason.trim() || mutation.isPending} data-emp-end-confirm
              onClick={() => mutation.mutate(
                { name: employee, relieving_date: date, reason_for_leaving: reason, new_workplace: workplace || undefined },
                { onSuccess: (res) => { setOrphans(res.orphaned_reports ?? []); setOpen(false); onDone(res); } })}
              className="w-full rounded bg-status-red px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
              {mutation.isPending ? t("emp.form.saving") : t("emp.end.confirm")}
            </button>
          </div>
        </div>
      )}
      {mutation.isError && <div className="mt-2"><ErrorBox error={mutation.error} /></div>}
      {orphans.length > 0 && (
        <p className="mt-2 text-[11px] text-status-amber" data-emp-end-orphans>
          {t("emp.end.orphans", { n: orphans.length, who: orphans.slice(0, 5).join(", ") })}
        </p>
      )}
    </Card>
  );
}
